<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <filter-bar
      ref="filterBarEl"
      :filters="filterSchema"
      :initial-value="filterValues"
      :result-count="totalRows"
      :per-page="perPage"
      :per-page-options="perPageOptions"
      saved-filters-key="email-messages"
      mobile-collapsible
      :sort-options="sortOptions"
      :sort-value="sort"
      :show-sort-chip="isMobile"
      @change="onFiltersChanged"
      @update:perPage="onPerPageChange"
      @update:sort="onSortChange"
    >
      <template #actions>
        <base-button v-if="canDownload" icon class="btn-link text-white" @click="downloadCsvExport">
          <i class="tim-icons icon-cloud-download-93"></i>
        </base-button>
      </template>
    </filter-bar>

    <data-table
      class="mt-3"
      :columns="columns"
      :rows="events"
      row-key="id"
      :loading="loading"
      clickable
      :page="currentPage"
      :per-page="perPage"
      :total="totalRows"
      :sort-value="sort"
      :default-sort="{ key: 'timestamp', dir: 'desc' }"
      @update:sort="onSortChange"
      @update:page="onPageChange"
      @row-click="openEvent"
    >
      <template #cell(timestamp)="{ row }">
        <span class="text-white">{{ formatWhen(row.timestamp) }}</span>
      </template>
      <template #cell(status)="{ row }">
        <span class="tag-chip text-white" :style="tagChipStyle(statusColor(row.latest_event))">{{ statusLabel(row.latest_event) }}</span>
      </template>
      <template #cell(recipient_email)="{ row }">
        <span class="text-white">{{ row.recipient_email }}</span>
        <small v-if="recipientName(row)" class="d-block text-white">{{ recipientName(row) }}</small>
      </template>
      <template #cell(email_name)="{ row }">
        {{ emailLabel(row) }}
        <small v-if="sourceLabel(row)" class="d-block text-white">{{ sourceLabel(row) }}</small>
      </template>
      <template #empty>
        <div class="empty-state">
          <img src="/img/pandas/empty-messages.png" class="empty-state-img" alt="No emails" />
          <h4 class="empty-state__title text-white">{{ hasActiveFilters ? 'No emails match these filters' : 'No emails sent yet' }}</h4>
          <p class="empty-state__hint">{{ hasActiveFilters ? 'Try clearing a filter to see more emails' : 'Every email this location sends is listed here with its delivery status' }}</p>
        </div>
      </template>
    </data-table>

    <email-log-drawer :show="drawerOpen" :event="selectedEvent" @close="drawerOpen = false" />
  </div>
</template>

<script setup lang="ts">
// Email tab, Every message view: one row per sent email of the current group, with the
// furthest SES event as its status (get-email-log-for-group). The drawer shows the detail.
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { tagChipStyle } from '@/util/tagChip'
import { pageOffset, readPagingQuery, syncPagingQuery, downloadCsv, fetchAllPages } from '@/util/listPage'
import EmailLogDrawer from './EmailLogDrawer.vue'
import { EMAIL_STATUS_LABELS, EMAIL_STATUS_COLORS, emailLabel, emailSourceLabel, eventToStatus } from './emailLogLabels'

const emit = defineEmits<{ (e: 'switch', allMessages: boolean): void }>()
const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/email-log`

const loading = ref(true)
const events = ref<any[]>([])
const totalRows = ref(0)
const currentPage = ref(1)
const perPage = ref(25)
const perPageOptions = [10, 25, 50, 100]
const sort = ref<{ key: string; dir: 'asc' | 'desc' }>({ key: 'timestamp', dir: 'desc' })
const sortOptions = [
  { key: 'timestamp', label: 'Sent' },
  { key: 'status', label: 'Status' }
]
const filterValues = ref<Record<string, any>>({ email: '', search: '', status: [], sent_date: null, every_message: true })
const isMobile = ref(false)
const drawerOpen = ref(false)
const selectedEvent = ref<any>(null)
let didInitialFetch = false

const filterSchema = computed(() => [
  { key: 'email', type: 'text', label: 'Recipient', placeholder: 'Customer email', operatorLabel: 'contains', primary: true },
  { key: 'search', type: 'text', label: 'Email', placeholder: 'Template or email type', operatorLabel: 'contains', primary: true },
  { key: 'status', type: 'multiselect', label: 'Status', primary: true,
    options: Object.keys(EMAIL_STATUS_LABELS).map((value) => ({ label: EMAIL_STATUS_LABELS[value], value })) },
  { key: 'sent_date', type: 'daterange', label: 'Sent date', primary: true },
  { key: 'every_message', type: 'toggle', label: 'Every message', primary: true }
])

const columns = [
  { key: 'timestamp', label: 'Sent', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'recipient_email', label: 'To' },
  { key: 'email_name', label: 'Email' }
]

const hasActiveFilters = computed(() => {
  const f = filterValues.value
  return !!(f.email || f.search || (f.status && f.status.length) || (f.sent_date && (f.sent_date.from || f.sent_date.to)))
})

const canDownload = computed(() => {
  try {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
    return Array.isArray(permissions) && permissions.includes('download_data')
  } catch (e) {
    return false
  }
})

function statusLabel(eventType: string) { return EMAIL_STATUS_LABELS[eventToStatus(eventType)] }
function statusColor(eventType: string) { return EMAIL_STATUS_COLORS[eventToStatus(eventType)] }
function formatWhen(value: string) {
  if (!value) return ''
  try {
    return formatGroupDate(value) + ' ' + formatGroupTime(value)
  } catch (e) {
    return String(value).slice(0, 16)
  }
}
function recipientName(row: any) {
  return [row.recipient_first_name, row.recipient_last_name].filter(Boolean).join(' ')
}
function sourceLabel(row: any) {
  return emailSourceLabel(row)
}

function queryString(count: number, offset: number) {
  const params = new URLSearchParams()
  params.append('count', String(count))
  params.append('offset', String(offset))
  params.append('sort', sort.value.key)
  params.append('dir', sort.value.dir)
  const f = filterValues.value
  if (f.email) params.append('email', f.email)
  if (f.search) params.append('search', f.search)
  if (Array.isArray(f.status) && f.status.length) params.append('status', f.status.join(','))
  if (f.sent_date && f.sent_date.from) params.append('date_from', f.sent_date.from)
  if (f.sent_date && f.sent_date.to) params.append('date_to', f.sent_date.to)
  return params.toString()
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

async function fetchPage(count: number, offset: number) {
  const config = await authConfig()
  const response = await axios.get(`${baseUrl}?${queryString(count, offset)}`, config)
  return { rows: response.data.events || [], total: response.data.total_rows || 0 }
}

async function loadEvents() {
  loading.value = true
  try {
    const page = await fetchPage(perPage.value, pageOffset(currentPage.value, perPage.value))
    events.value = page.rows
    totalRows.value = page.total
    syncPagingQuery(proxy.$router, proxy.$route, currentPage.value, perPage.value)
    didInitialFetch = true
  } catch (err: any) {
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      proxy.$router.push('/forbidden')
      return
    }
    proxy.$notify({ type: 'danger', message: 'Could not load the email log. Please refresh the page.', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

// The first change after mount is FilterBar's restore (last-used or default view). The
// last-used save is debounced, so a restore can still carry the other view's toggle state
// for a moment; it never switches views. Only an explicitly opened saved view (?view= on
// mount) or a later user change does.
const presetOnMount = !!proxy.$route.query.view
let restored = false
const filterBarEl = ref<any>(null)
function settleView(values: Record<string, any>, here: boolean): boolean {
  const first = !restored
  restored = true
  if ((values.every_message === true) === here) return true
  if (first && !presetOnMount) {
    filterValues.value.every_message = here
    if (filterBarEl.value) filterBarEl.value.applyValues({ ...values, every_message: here })
    return true
  }
  emit('switch', !here)
  return false
}
function onFiltersChanged(values: Record<string, any>) {
  filterValues.value = { ...filterValues.value, ...values }
  if (!settleView(filterValues.value, true)) return
  currentPage.value = 1
  loadEvents()
}
function onPerPageChange(n: number, meta?: { restoring?: boolean }) {
  perPage.value = n
  currentPage.value = 1
  if (meta && meta.restoring) return
  loadEvents()
}
function onSortChange(value: { key: string; dir: 'asc' | 'desc' }, meta?: { restoring?: boolean }) {
  sort.value = { key: value.key, dir: value.dir === 'asc' ? 'asc' : 'desc' }
  if (meta && meta.restoring) return
  currentPage.value = 1
  if (didInitialFetch) loadEvents()
}
function onPageChange(page: number) {
  currentPage.value = page
  loadEvents()
}
function openEvent(row: any) {
  selectedEvent.value = row
  drawerOpen.value = true
}

async function downloadCsvExport() {
  loading.value = true
  try {
    // 1,000 rows per request (the lambda's cap) so a season of mail is a handful of calls
    const rows = await fetchAllPages(fetchPage, 1000)
    downloadCsv('email_log.csv', [
      { label: 'Sent', value: (row: any) => formatGroupDate(row.timestamp, { dayOfWeek: false }) + ' ' + formatGroupTime(row.timestamp) },
      { label: 'Status', value: (row: any) => statusLabel(row.latest_event) },
      { label: 'Last event', value: (row: any) => (row.latest_event_at ? formatGroupDate(row.latest_event_at, { dayOfWeek: false }) + ' ' + formatGroupTime(row.latest_event_at) : '') },
      { label: 'To', value: (row: any) => row.recipient_email || '' },
      { label: 'Recipient', value: (row: any) => recipientName(row) },
      { label: 'Email', value: (row: any) => emailLabel(row) },
      { label: 'Subject', value: (row: any) => row.subject_line || '' },
      { label: 'Source', value: (row: any) => emailSourceLabel(row) },
      { label: 'Transaction', value: (row: any) => row.transaction_id || '' }
    ], rows)
  } catch (err) {
    proxy.$notify({ type: 'danger', message: 'Could not export the email log. Please try again.', icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    loading.value = false
  }
}

const mobileMql = window.matchMedia('(max-width: 767px)')
const onMobileMql = () => { isMobile.value = mobileMql.matches }

onMounted(() => {
  onMobileMql()
  mobileMql.addEventListener('change', onMobileMql)
  const paging = readPagingQuery(proxy.$route, 25)
  perPage.value = perPageOptions.includes(paging.perPage) ? paging.perPage : 25
  currentPage.value = paging.page
  loadEvents()
})
onBeforeUnmount(() => {
  mobileMql.removeEventListener('change', onMobileMql)
})
</script>
